import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { compact, totals } from "../analytics";
import { DataTable } from "../components/DataTable";
import { LineChart, type LineSeries } from "../components/LineChart";
import { bucketHeader, bucketLabeller, perWord, RangeControls } from "../components/RangeControls";
import { dayLabel } from "../earlyAccess";
import { latestDay, netGrowth, rangeDays, seriesFor, type Bucket, type RangeId } from "../growth";
import { errorText } from "../problem";
import { readSeries } from "../seriesApi";
// Each surface keeps its colour whichever range is shown.
const SURFACES = [
  { id: "android", label: "Android", tone: "series-1" },
  { id: "web", label: "Web", tone: "series-2" },
  { id: "ha", label: "Home Assistant", tone: "series-3" },
  { id: "api_token", label: "API tokens", tone: "series-4" },
  { id: "ios", label: "iPhone", tone: "series-5" },
] as const;

/** The page's four series, read together so a range or bucket change never mixes old and new ones. */
function useGrowth(from: string, to: string, bucket: Bucket) {
  return useQuery({
    queryKey: ["analytics-growth", from, to, bucket],
    queryFn: async () => {
      const [signups, deletions, active, surfaces] = await Promise.all([
        readSeries("signups", from, to, bucket),
        readSeries("deletions", from, to, bucket),
        readSeries("active", from, to, bucket),
        readSeries("active_surface", from, to, bucket, { group: "surface" }),
      ]);
      return { from, to, bucket, signups, deletions, active, surfaces };
    },
    placeholderData: keepPreviousData,
    staleTime: 10 * 60_000,
  });
}

export function GrowthPage() {
  const [range, setRange] = useState<RangeId>("90d");
  const [bucket, setBucket] = useState<Bucket>("week");
  const days = rangeDays(range, bucket, latestDay());
  const growth = useGrowth(days.from, days.to, bucket);
  // While a new range loads, the previous one stays on screen with its own labels.
  const data = growth.data;
  const shown = data?.bucket ?? bucket;
  const per = perWord(shown);
  const bucketLabel = bucketLabeller(shown);
  return (
    <>
      <h1>Growth</h1>
      <section className="stack">
        <RangeControls range={range} bucket={bucket} onRange={setRange} onBucket={setBucket} />
        {growth.isError ? (
          <p className="error">{errorText(growth.error)}</p>
        ) : !data ? (
          <p>Loading…</p>
        ) : (
          <GrowthCharts
            from={data.from}
            to={data.to}
            bucket={data.bucket}
            per={per}
            bucketLabel={bucketLabel}
            buckets={data.signups.buckets}
            signups={totals(data.signups)}
            deletions={totals(data.deletions)}
            active={totals(data.active)}
            bySurface={SURFACES.map((surface) => ({
              key: surface.id,
              label: surface.label,
              tone: surface.tone,
              values: seriesFor(data.surfaces, "surface", surface.id),
            }))}
            builtThrough={data.signups.built_through}
          />
        )}
      </section>
    </>
  );
}

function GrowthCharts({
  from,
  to,
  bucket,
  per,
  bucketLabel,
  buckets,
  signups,
  deletions,
  active,
  bySurface,
  builtThrough,
}: {
  from: string;
  to: string;
  bucket: Bucket;
  per: string;
  bucketLabel: (start: string) => string;
  buckets: readonly string[];
  signups: (number | null)[];
  deletions: (number | null)[];
  active: (number | null)[];
  bySurface: LineSeries[];
  builtThrough: string | null;
}) {
  const net = netGrowth(signups, deletions);
  const sum = (values: (number | null)[]) => values.reduce<number>((total, v) => total + (v ?? 0), 0);
  const averaged = (value: number) => (Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1));
  return (
    <>
      <p className="muted">
        UTC days from {dayLabel(from)} through {dayLabel(to)}
        {builtThrough
          ? `; the rollup has built through ${dayLabel(builtThrough)}`
          : "; the rollup has built nothing yet"}
        . Sign-ups include accounts deleted since.
      </p>
      <div className="charts charts-wide">
        <LineChart
          title={`Sign-ups and deletions per ${per}`}
          note={`${compact(sum(signups))} sign-ups, ${compact(sum(deletions))} deletions`}
          buckets={buckets}
          bucketLabel={bucketLabel}
          series={[
            { key: "signups", label: "Sign-ups", tone: "series-1", values: signups },
            { key: "deletions", label: "Deletions", tone: "series-2", values: deletions },
          ]}
        />
        <LineChart
          title={`Net growth per ${per}`}
          note={`${sum(net) >= 0 ? "+" : "−"}${compact(Math.abs(sum(net)))} in all`}
          buckets={buckets}
          bucketLabel={bucketLabel}
          series={[{ key: "net", label: "Net growth", tone: "series-1", values: net }]}
        />
        <LineChart
          title={bucket === "day" ? "Active users by surface" : `Average daily active users per ${per}, by surface`}
          note="anyone using several counts on each"
          buckets={buckets}
          bucketLabel={bucketLabel}
          format={averaged}
          series={[{ key: "any", label: "Any surface", tone: "muted", values: active }, ...bySurface]}
        />
      </div>
      <DataTable
        header={[
          bucketHeader(bucket),
          "Sign-ups",
          "Deletions",
          "Net growth",
          "Active (any)",
          ...bySurface.map((s) => `Active (${s.label})`),
        ]}
        rows={buckets
          .map((start, i) => [
            start,
            signups[i] ?? null,
            deletions[i] ?? null,
            net[i] ?? null,
            active[i] ?? null,
            ...bySurface.map((s) => s.values[i] ?? null),
          ])
          .toReversed()}
        fileName={`ten20-growth-${from}-${to}-${bucket}.csv`}
        format={averaged}
      />
    </>
  );
}
